import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { fileUrl } from "@/lib/api";
import { AMBIENT_TRACKS } from "@/data/ambient";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const ambientRef = useRef(null);

  // Eagerly initialize audio elements so they're available before children's mount effects fire.
  if (typeof window !== "undefined" && audioRef.current === null) {
    audioRef.current = document.createElement("audio");
    audioRef.current.preload = "metadata";
  }
  if (typeof window !== "undefined" && ambientRef.current === null) {
    ambientRef.current = document.createElement("audio");
    ambientRef.current.preload = "auto";
    ambientRef.current.volume = 0.35;
  }

  const [track, setTrack] = useState(null); // beat currently playing
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  // Ambient state — user-controlled background music
  const [ambientOn, setAmbientOn] = useState(false); // user's INTENT
  const [ambientIdx, setAmbientIdx] = useState(0);
  const [ambientPlaying, setAmbientPlaying] = useState(false); // actually audible right now
  const [ambientVolume, setAmbientVolume] = useState(0.35);
  const [ambientMuted, setAmbientMuted] = useState(false);

  // ---- Beat audio element ----
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setProgress(el.currentTime);
    const onDur = () => setDuration(el.duration || 0);
    const onEnd = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onDur);
    el.addEventListener("ended", onEnd);
    return () => {
      el.pause();
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onDur);
      el.removeEventListener("ended", onEnd);
    };
  }, []);

  // ---- Ambient audio element ----
  useEffect(() => {
    const el = ambientRef.current;
    if (!el) return;
    const onEnd = () => {
      // move to next track in rotation
      setAmbientIdx((i) => (i + 1) % AMBIENT_TRACKS.length);
    };
    el.addEventListener("ended", onEnd);
    return () => {
      el.pause();
      el.removeEventListener("ended", onEnd);
    };
  }, []);

  // Sync ambient volume changes
  useEffect(() => {
    if (ambientRef.current) ambientRef.current.volume = ambientVolume;
  }, [ambientVolume]);

  // Sync ambient muted state
  useEffect(() => {
    if (ambientRef.current) ambientRef.current.muted = ambientMuted;
  }, [ambientMuted]);

  // Play the current ambient track when idx changes AND (ambientOn AND no beat playing)
  useEffect(() => {
    const el = ambientRef.current;
    if (!el) return;
    el.src = AMBIENT_TRACKS[ambientIdx].url;
    if (ambientOn && !playing) {
      el.play().then(() => setAmbientPlaying(true)).catch(() => setAmbientPlaying(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambientIdx]);

  // Duck ambient whenever a beat is playing; resume when beat stops (if ambientOn)
  useEffect(() => {
    const el = ambientRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setAmbientPlaying(false);
    } else if (ambientOn) {
      if (!el.src) el.src = AMBIENT_TRACKS[ambientIdx].url;
      el.play().then(() => setAmbientPlaying(true)).catch(() => setAmbientPlaying(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, ambientOn]);

  // ---- Beat controls ----
  const play = (newTrack) => {
    const el = audioRef.current;
    if (!el) return;
    if (track && track.id === newTrack.id) {
      if (el.paused) { el.play(); setPlaying(true); }
      else { el.pause(); setPlaying(false); }
      return;
    }
    el.src = fileUrl(newTrack.audio_path);
    setTrack(newTrack);
    el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  const toggle = () => {
    const el = audioRef.current;
    if (!el || !track) return;
    if (el.paused) { el.play(); setPlaying(true); }
    else { el.pause(); setPlaying(false); }
  };

  const seek = (pct) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    el.currentTime = pct * duration;
  };

  // ---- Ambient controls ----
  const toggleAmbient = useCallback(() => {
    setAmbientOn((prev) => {
      const next = !prev;
      const el = ambientRef.current;
      if (!el) return next;
      if (next) {
        if (!el.src) el.src = AMBIENT_TRACKS[ambientIdx].url;
        if (!playing) el.play().then(() => setAmbientPlaying(true)).catch(() => setAmbientPlaying(false));
      } else {
        el.pause();
        setAmbientPlaying(false);
      }
      return next;
    });
  }, [ambientIdx, playing]);

  const nextAmbient = () => setAmbientIdx((i) => (i + 1) % AMBIENT_TRACKS.length);

  // Attempt muted autoplay (browsers allow this without user gesture).
  // We set ambientOn=true optimistically so the UNMUTE prompt appears even if
  // the browser silently blocked the muted autoplay attempt — one click will
  // then start audio reliably.
  const autoStartAmbient = useCallback(() => {
    const el = ambientRef.current;
    if (!el || ambientOn) return;
    el.muted = true;
    setAmbientMuted(true);
    setAmbientOn(true);
    if (!el.src) el.src = AMBIENT_TRACKS[ambientIdx].url;
    el.play()
      .then(() => setAmbientPlaying(true))
      .catch(() => setAmbientPlaying(false));
  }, [ambientOn, ambientIdx]);

  const unmuteAmbient = useCallback(() => {
    const el = ambientRef.current;
    if (!el) return;
    el.muted = false;
    setAmbientMuted(false);
    // If autoplay was blocked entirely, ensure it plays now
    if (!ambientOn) {
      if (!el.src) el.src = AMBIENT_TRACKS[ambientIdx].url;
      el.play().then(() => {
        setAmbientOn(true);
        setAmbientPlaying(true);
      }).catch(() => {});
    }
  }, [ambientOn, ambientIdx]);

  return (
    <PlayerContext.Provider
      value={{
        // beat
        track, playing, progress, duration, play, toggle, seek,
        // ambient
        ambientOn, ambientPlaying, ambientMuted, ambientTrack: AMBIENT_TRACKS[ambientIdx],
        ambientVolume, setAmbientVolume, toggleAmbient, nextAmbient,
        autoStartAmbient, unmuteAmbient,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  return useContext(PlayerContext);
}
